import { useEntityFilter } from '@risksmart-app/components/src/contexts/entityFilterContext';
import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import type { AcceptanceDashboardDateField } from '@risksmart-app/domain/src/types/consts/dashboard-date-fields';
import { ParentTypes as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import type { AcceptanceRegisterResponseRow } from '@risksmart-app/drizzle/src/dto';
import type {
  Acceptance_Bool_Exp,
  GetAcceptancesQuery,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { GetAcceptancesDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { createQueryHook, type StrictMappedGraphQLSelection } from 'src/utils';

import { ACCEPTANCE_DATE_PREDICATES } from '@/hooks/queries/dashboardDatePredicates';
import {
  buildDashboardWhere,
  type DashboardFilterArgs,
  normaliseArrayFilter,
} from '@/hooks/queries/dashboardFilterArgs';
import useEntityWhereFilter from '@/hooks/useEntityWhereFilter';

const useAcceptancesGraphqlVariables = (args: InternalArgs) => {
  const { user } = useRisksmartUser();
  const entityWhere = useEntityWhereFilter<Acceptance_Bool_Exp>(
    Parent_Type_Enum.Acceptance
  );

  const { where } = buildDashboardWhere({
    base: entityWhere,
    filters: args,
    userId: user?.userId,
    datePredicates: ACCEPTANCE_DATE_PREDICATES,
    tagsWhere: (ids) => ({
      parents: { risk: { tags: { TagTypeId: { _in: ids } } } },
    }),
    departmentsWhere: (ids) => ({
      parents: {
        risk: { departments: { DepartmentTypeId: { _in: ids } } },
      },
    }),
    ownershipWhere: (conditions) => ({
      parents: { risk: conditions.riskFilterConditions },
    }),
  });

  return { where };
};

type InternalArgs = UseGetAcceptancesRegisterArgs & {
  entityIds?: string[];
};

/**
 * Maps TRPC acceptance data to match the GraphQL query structure
 */
export function mapTrpcAcceptancesToGraphQL(
  trpcData: AcceptanceRegisterResponseRow[]
): StrictMappedGraphQLSelection<GetAcceptancesQuery> {
  return {
    acceptance: trpcData,
  };
}

export type UseGetAcceptancesRegisterArgs =
  DashboardFilterArgs<AcceptanceDashboardDateField>;

const useGetAcceptancesRegisterBase = createQueryHook<
  InternalArgs,
  AcceptanceRegisterResponseRow[],
  GetAcceptancesQuery
>({
  trpcQueryOptions: (
    trpc,
    { entityIds, tagTypeIds, departmentTypeIds, dateRange, ownership }
  ) =>
    trpc.frontend.acceptance.register.queryOptions({
      entityIds,
      tagTypeIds: normaliseArrayFilter(tagTypeIds),
      departmentTypeIds: normaliseArrayFilter(departmentTypeIds),
      dateRange,
      ownership,
    }),
  mapTrpcDataToGraphQL: mapTrpcAcceptancesToGraphQL,
  graphqlDocument: GetAcceptancesDocument,
  graphqlVariables: useAcceptancesGraphqlVariables,
});

/**
 * Fetches the acceptances register with automatic entity filtering for both
 * GraphQL and tRPC paths. Entity filtering uses useEntityFilter context
 * internally — consumers don't need to pass entityIds. Dashboard filters
 * (COI-691) are all optional.
 */
export const useGetAcceptancesRegister = (params: {
  queryArgs: UseGetAcceptancesRegisterArgs;
  shouldSkip?: boolean;
  suppressErrorNotification?: boolean;
}) => {
  const { entityIds } = useEntityFilter();

  return useGetAcceptancesRegisterBase({
    queryArgs: {
      ...params.queryArgs,
      entityIds: entityIds?.length ? entityIds : undefined,
    },
    shouldSkip: params.shouldSkip,
    suppressErrorNotification: params.suppressErrorNotification,
  });
};
